<template>
	<PageTitleComponent>Survey Creator</PageTitleComponent>

	<section class="band">
		<p>This is where you can create survey links and QR codes to distribute to your schools.</p>
		<p>Please choose the survey and school from the options below.</p>

		<form action="#">
			<div class="form-group">
				<label
					class="label"
					for="survey"
					><strong>Which survey do you want to create a link for?:</strong></label
				>
				<div class="select-container">
					<select
						id="survey"
						name="survey"
						v-model="linkOptions.survey"
						required
					>
						<option value="">Select a survey</option>
						<option
							v-for="(s, key) in surveys"
							:key="key"
							:value="key"
						>
							{{ s.name }}
						</option>
					</select>
				</div>

				<div
					v-if="survey"
					class="responses"
				>
					<p>
						<small>
							<a
								:href="survey.responses"
								target="_blank"
								>View responses
							</a>
							Don't delete or modify this file. You may filter using the column filters at the top.
						</small>
					</p>
				</div>
			</div>
			<div class="form-group">
				<label
					class="label"
					for="schoolId"
					><strong>For which school?:</strong></label
				>
				<div class="select-container">
					<select
						id="schoolId"
						name="schoolId"
						v-model="linkOptions.school"
						required
					>
						<option value="">Select a school</option>
						<option
							v-for="school in schools"
							:key="school.ID"
							:value="school.post_title"
						>
							{{ school.post_title }}
						</option>
					</select>
				</div>
			</div>

			<div
				v-if="link"
				class="content link"
			>
				<hr />
				<h3>Generated</h3>
				<div class="form-group">
					<label
						class="label"
						for="link"
						><strong>Link:</strong></label
					>
					<div class="link-plain">
						<a
							:href="link"
							target="_blank"
							rel="noopener noreferrer"
							>{{ link }}</a
						>
					</div>
					<div class="link-copy">
						<ClickToCopyToClipboardComponent
							:text-override="link"
							class="btn"
							>Click to copy link</ClickToCopyToClipboardComponent
						>
					</div>
				</div>
				<div
					class="form-group link-qr"
					ref="qrContainer"
					v-if="link"
				>
					<qrcode-vue
						:value="link"
						:size="300"
						:margin="2"
					/>

					<button
						@click.prevent="handleDownloadQr"
						class="btn"
					>
						Download QR Code
					</button>
				</div>
			</div>
		</form>

		<LoadingComponent
			:loading="loading"
			:outer-border="true"
		/>
	</section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import LoadingComponent from '@/components/shared/LoadingComponent.vue';
import PageTitleComponent from '@/components/shared/PageTitleComponent.vue';
import { mapState } from 'pinia';
import { useUser } from '@/stores/userStore';
import ClickToCopyToClipboardComponent from '@/components/shared/ClickToCopyToClipboardComponent.vue';
import QrcodeVue from 'qrcode.vue';

export default defineComponent({
	components: { LoadingComponent, PageTitleComponent, ClickToCopyToClipboardComponent, QrcodeVue },
	data() {
		return {
			loading: true,
			linkOptions: {
				survey: '',
				school: '',
			},
			surveys: {
				'parent-engagement-session-feedback': {
					name: 'Parent Engagement Session Feedback',
					link: 'https://forms.office.com/Pages/ResponsePage.aspx?id=7ekjUQRqVkWRXxJDx17ntfhmAvqBpI9Jq6c_ILsLIsFUMjVQT0pFTVcxRjFVTVRDRDdGVUZXWFI2Vi4u&ra16abc5f3efc44919de0587e63b66b7d=',
					responses: 'https://realschools-my.sharepoint.com/:x:/g/personal/nick_realschools_com_au/IQD_FTe6lu_QT6Ch1XjGHVrAAfsm1pS7Bt4ErZ0pBtLwYg0?e=8RpD0S',
				},
				'getting-ready-for-professional-learning': {
					name: 'Getting Ready for Professional Learning',
					link: 'https://forms.office.com/Pages/ResponsePage.aspx?id=7ekjUQRqVkWRXxJDx17ntfhmAvqBpI9Jq6c_ILsLIsFUMDhTOVdNNEk2SlVSSUlaMlRFWVQ1VElXVy4u&re71bb8191b9f44a59f13d7c53ca2d85e=',
					responses: 'https://realschools-my.sharepoint.com/:x:/g/personal/nick_realschools_com_au/EYa1cXxdnodLl2xdNVVb3MABYtwD9TJL0SPeSoEG4Qbe2Q?e=J7PnrJ',
				},
				'implementation-evaluation-staff-survey': {
					name: 'Implementation Evaluation Staff Survey',
					link: 'https://forms.office.com/Pages/ResponsePage.aspx?id=7ekjUQRqVkWRXxJDx17ntfhmAvqBpI9Jq6c_ILsLIsFURVA4T0NCR1c0SUhNTlJEVzdIT1M1MzFGTy4u&re5aeaecd07f54f5b8e5f34306af01344=',
					responses: 'https://realschools-my.sharepoint.com/:x:/g/personal/nick_realschools_com_au/Eequd-Ykv69Msw192k9ousMBjmfPxn0gU3jI84FqjcFXeg?e=NTMU2v',
				},
			} as Record<string, Survey>,
		};
	},
	computed: {
		survey() {
			const survey = this.surveys[this.linkOptions.survey];
			return survey || null;
		},
		link() {
			if (!this.linkOptions.survey || !this.linkOptions.school) {
				return '';
			}

			if (!this.survey) {
				return '';
			}

			const schoolEncoded = encodeURIComponent(this.linkOptions.school);

			return `${this.survey.link}${schoolEncoded}`;
		},
		...mapState(useUser, ['schools', 'school']),
	},
	mounted() {
		this.loading = false;
		if (this.school) {
			this.linkOptions.school = this.school.post_title;
		}
	},
	methods: {
		handleDownloadQr() {
			const qrContainer = this.$refs.qrContainer as HTMLElement;
			if (!qrContainer) {
				return;
			}

			const qrElement = qrContainer.querySelector('canvas');
			if (!qrElement) {
				return;
			}

			const link = document.createElement('a');
			link.href = qrElement.toDataURL('image/png');
			link.download = `QR - ${this.linkOptions.survey} - ${this.linkOptions.school}.png`;
			link.click();
		},
	},
});

interface Survey {
	name: string;
	link: string;
	responses: string;
}
</script>

<style scoped>
form {
	margin-top: 40px;
	max-width: 600px;
}

.responses {
	margin-top: 7px;
}

.link-plain {
	word-break: break-all;
}

.link-copy {
	margin-top: 20px;
}

.link-qr {
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-top: 40px;
}
</style>
